import { Link } from "react-router-dom";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useSEO } from "../hooks/useSEO";
import { money, shippingFor } from "../config/brand";

export default function CartPage() {
  const { items, setQty, remove, subtotal } = useCart();
  useSEO("CART — RICH KIDS FUNERAL", "Your RICH KIDS FUNERAL cart.");
  const shipping = shippingFor(subtotal);

  return (
    <div className="px-3 md:px-6 pt-8 md:pt-12 max-w-5xl" data-testid="cart-page">
      <h1 className="font-anton-sc text-2xl md:text-4xl tracking-[0.08em]">CART</h1>

      {items.length === 0 ? (
        <div className="py-24" data-testid="cart-page-empty">
          <p className="text-[11px] tracking-[0.2em] text-black/50">YOUR CART IS EMPTY</p>
          <Link
            to="/all-products"
            data-testid="cart-continue-shopping"
            className="inline-block mt-8 text-[11px] font-medium tracking-[0.2em] border border-black px-10 py-4 hover:bg-black hover:text-white transition-colors"
          >
            CONTINUE SHOPPING
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-[1fr_320px] gap-12 mt-10">
          <div>
            {items.map((i) => (
              <div key={i.key} className="flex gap-5 py-6 border-b border-black/10" data-testid={`cart-page-item-${i.slug}`}>
                <Link to={`/products/${i.slug}`} className="w-24 flex-shrink-0">
                  <div className="aspect-[1/1] bg-white overflow-hidden">
                    {i.image && (
                      <img src={i.image} alt={i.name} className="w-full h-full object-contain" loading="lazy" />
                    )}
                  </div>
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-3">
                    <Link
                      to={`/products/${i.slug}`}
                      className="text-[12px] font-medium tracking-[0.08em] uppercase hover:opacity-50 transition-opacity"
                    >
                      {i.name}
                    </Link>
                    <button
                      data-testid={`cart-page-remove-${i.slug}`}
                      onClick={() => remove(i.key)}
                      aria-label={`Remove ${i.name}`}
                      className="text-black/40 hover:text-black transition-colors"
                    >
                      <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                  {i.size && (
                    <p className="text-[10px] text-black/50 mt-1 tracking-[0.1em]">SIZE {i.size}</p>
                  )}
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center border border-black/20">
                      <button
                        data-testid={`cart-page-qty-minus-${i.slug}`}
                        onClick={() => setQty(i.key, i.qty - 1)}
                        className="w-8 h-8 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-9 text-center text-[12px]">{i.qty}</span>
                      <button
                        data-testid={`cart-page-qty-plus-${i.slug}`}
                        onClick={() => setQty(i.key, i.qty + 1)}
                        className="w-8 h-8 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-[12px]">{money(i.price * i.qty)}</p>
                  </div>
                </div>
              </div>
            ))}
            <Link
              to="/all-products"
              className="inline-block mt-6 text-[10px] tracking-[0.2em] underline underline-offset-4 hover:opacity-50 transition-opacity"
            >
              CONTINUE SHOPPING
            </Link>
          </div>
          <div className="self-start border border-black/15 p-6" data-testid="cart-summary">
            <p className="text-[11px] font-medium tracking-[0.2em] mb-5">ORDER SUMMARY</p>
            <div className="space-y-2 text-[12px]">
              <div className="flex justify-between">
                <span className="text-black/60">Subtotal</span>
                <span data-testid="cart-page-subtotal">{money(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-black/60">Shipping</span>
                <span data-testid="cart-page-shipping">{shipping === 0 ? "FREE" : money(shipping)}</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-black/10 font-medium">
                <span>Total</span>
                <span data-testid="cart-page-total">{money(subtotal + shipping)}</span>
              </div>
            </div>
            <Link
              to="/checkout"
              data-testid="cart-page-checkout-button"
              className="block w-full bg-black text-white text-center text-[11px] font-medium tracking-[0.2em] py-4 mt-6 hover:bg-[#e50000] transition-colors"
            >
              CHECKOUT
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
